import { errorForStatus, ForbiddenError, HonestyError, NetworkError, type ApiError } from './errors';
import { noteAnswer } from '../i18n/names';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type QueryValue = string | number | boolean | null | undefined;

export interface ApiClientOptions {
  /**
   * Prefix for every path, for example '' (same origin, the dev proxy) or
   * 'https://api.example.com'. Paths are absolute from /api.
   */
  baseUrl?: string;
  /** Returns the current session token, or null when signed out. Read on every call. */
  getToken?: () => string | null;
  /** Called once per 401 on an authenticated call, so the app can drop the session. */
  onUnauthorized?: (err: ApiError) => void;
  /** Injected for tests and React Native. Defaults to the global fetch. */
  fetch?: typeof fetch;
  /**
   * "View as" (Super Admin only): the role the console is currently rendering, or null.
   * While set, every call carries it in X-Aithora-View-As, and every call that could
   * change something is refused here, before it leaves the browser.
   */
  getViewAs?: () => string | null;
}

export interface RequestOptions {
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Statuses whose JSON body is a normal answer rather than an error. Login uses
   * this: a 401 with `twofaRequired` is the next step of signing in, not a failure.
   */
  acceptStatuses?: readonly number[];
  /** The route promises the `real` honesty flag; refuse an answer without it. */
  honest?: boolean;
  /** Do not send the Bearer token (sign-in calls). */
  anonymous?: boolean;
  /**
   * A non-GET call that changes nothing anyone else sees (sign out, asking Help), so it
   * still goes through while viewing as another role.
   */
  readOnlySafe?: boolean;
  /**
   * Sent as the Idempotency-Key header: the same key on a write sent again (the phone's
   * offline queue after a timeout) gets the first answer back, never a second write.
   */
  idempotencyKey?: string;
  /**
   * Give up after this long with a NetworkError (status 0), the same as no signal. Unset:
   * wait for the answer however long it takes (as before).
   */
  timeoutMs?: number;
}

/** The header a write's idempotency key travels in (src/aithora/idempotency.ts). */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/** The header that tells the server which role the console is viewing as. */
export const VIEW_AS_HEADER = 'X-Aithora-View-As';

export interface ApiResponse<T> {
  status: number;
  data: T;
  requestId: string | null;
}

function buildQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** The server's error sentence, or a plain fallback. */
function messageFrom(body: unknown, status: number): string {
  if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
    return (body as { error: string }).error;
  }
  if (status >= 500) return 'The server had a problem answering. Try again.';
  return `The request failed (${status}).`;
}

/** The refusal a write gets while viewing as another role: nothing is sent. */
function viewAsRefusal(method: string, path: string, viewAs: string): ForbiddenError {
  return new ForbiddenError({
    kind: 'forbidden', status: 0, method, path, body: { viewAs },
    message: 'Viewing as another role is read only, so nothing was sent.',
  });
}

/**
 * A small fetch-based client. It adds the Bearer token, turns every failure into a typed
 * ApiError carrying the request id, and checks the honesty flag on routes that promise it.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: () => string | null;
  private readonly onUnauthorized?: (err: ApiError) => void;
  private readonly fetchImpl: typeof fetch;
  private readonly getViewAs: () => string | null;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = (opts.baseUrl ?? '').replace(/\/+$/, '');
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
    this.getViewAs = opts.getViewAs ?? (() => null);
    const f = opts.fetch ?? (typeof fetch === 'function' ? fetch : undefined);
    if (!f) throw new Error('No fetch available; pass one in ApiClientOptions.fetch');
    // Bind so a global fetch is never called with the client as `this`.
    this.fetchImpl = f.bind(globalThis) as typeof fetch;
  }

  url(path: string, query?: Record<string, QueryValue>): string {
    return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}${buildQuery(query)}`;
  }

  async request<T>(method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = opts.anonymous ? null : this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const viewAs = opts.anonymous ? null : this.getViewAs();
    if (viewAs) {
      if (method !== 'GET' && !opts.readOnlySafe) throw viewAsRefusal(method, path, viewAs);
      headers[VIEW_AS_HEADER] = viewAs;
    }
    if (opts.idempotencyKey) headers[IDEMPOTENCY_HEADER] = opts.idempotencyKey;
    let body: string | undefined;
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }

    let res: Response;
    // timeoutMs: our own abort, told apart from the caller's, and reported as no answer.
    const timer = opts.timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timedOut = false;
    const onCallerAbort = () => timer?.abort();
    if (timer && opts.signal) {
      if (opts.signal.aborted) timer.abort();
      else opts.signal.addEventListener('abort', onCallerAbort);
    }
    const clock = timer ? setTimeout(() => { timedOut = true; timer.abort(); }, opts.timeoutMs) : null;
    try {
      res = await this.fetchImpl(this.url(path, opts.query), { method, headers, body, signal: timer ? timer.signal : opts.signal });
    } catch (cause) {
      if (timedOut) {
        throw new NetworkError({
          kind: 'network', status: 0, method, path, body: cause,
          message: 'Aithora did not answer in time. Check the connection and try again.',
        });
      }
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new NetworkError({
        kind: 'network', status: 0, method, path,
        message: 'Could not reach Aithora. Check the connection and try again.',
        body: cause,
      });
    } finally {
      if (clock) clearTimeout(clock);
      opts.signal?.removeEventListener?.('abort', onCallerAbort);
    }

    const requestId = res.headers.get('x-request-id');
    const text = await res.text().catch(() => '');
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
        if (res.ok) {
          throw new NetworkError({
            kind: 'network', status: res.status, method, path, requestId, body: text,
            message: 'The server sent an answer the console could not read.',
          });
        }
      }
    }

    const accepted = opts.acceptStatuses?.includes(res.status) ?? false;
    if (!res.ok && !accepted) {
      const err = errorForStatus(res.status, { message: messageFrom(parsed, res.status), requestId, body: parsed, method, path });
      if (res.status === 401 && token && this.onUnauthorized) this.onUnauthorized(err);
      throw err;
    }

    if (opts.honest && res.ok) {
      const real = parsed && typeof parsed === 'object' ? (parsed as { real?: unknown }).real : undefined;
      if (typeof real !== 'boolean') {
        throw new HonestyError({
          kind: 'honesty', status: res.status, method, path, requestId, body: parsed,
          message: 'The server did not say whether this data is real or sample, so it is not shown.',
        });
      }
    }

    // People's names in the answer are kept out of the Spanish pass (i18n/names.ts).
    noteAnswer(parsed);
    return { status: res.status, data: parsed as T, requestId };
  }

  /**
   * A plain fetch with the base URL and the Bearer token added, for answers the JSON
   * path cannot read (the Atlas SSE stream). The caller handles status and body.
   */
  raw(path: string, init: RequestInit = {}): Promise<Response> {
    let headers: Record<string, string>;
    try {
      headers = this.rawHeaders(path, String(init.method ?? 'GET'), (init.headers as Record<string, string>) ?? {});
    } catch (e) {
      return Promise.reject(e);
    }
    return this.fetchImpl(this.url(path), { ...init, headers });
  }

  /**
   * The headers `raw` sends (the Bearer token and View as), for a caller that sends the
   * request itself (the phone streams Atlas over XMLHttpRequest). Throws the View as refusal
   * for a write, exactly as `raw` refuses it.
   */
  rawHeaders(path: string, method: string, extra: Record<string, string> = {}): Record<string, string> {
    const token = this.getToken();
    const headers: Record<string, string> = { ...extra };
    if (token) headers.Authorization = `Bearer ${token}`;
    const viewAs = this.getViewAs();
    if (viewAs) {
      const m = method.toUpperCase();
      if (m !== 'GET') throw viewAsRefusal(m, path, viewAs);
      headers[VIEW_AS_HEADER] = viewAs;
    }
    return headers;
  }

  get<T>(path: string, opts?: RequestOptions) { return this.request<T>('GET', path, opts); }
  post<T>(path: string, body?: unknown, opts?: RequestOptions) { return this.request<T>('POST', path, { ...opts, body }); }
  put<T>(path: string, body?: unknown, opts?: RequestOptions) { return this.request<T>('PUT', path, { ...opts, body }); }
  patch<T>(path: string, body?: unknown, opts?: RequestOptions) { return this.request<T>('PATCH', path, { ...opts, body }); }
  delete<T>(path: string, opts?: RequestOptions) { return this.request<T>('DELETE', path, opts); }
}
